"use strict";
var _a, _b, _c, _d, _e, _f, _g, _h, _j;
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContactService = void 0;
const tslib_1 = require("tslib");
const common_1 = require("@nestjs/common");
const typeorm_1 = require("typeorm");
const contact_repository_1 = require("./contact.repository");
const address_repository_1 = require("../address/address.repository");
const company_repository_1 = require("../company/company.repository");
const look_up_repository_1 = require("../look-up/look-up.repository");
const insurer_repository_1 = require("../insurer/insurer.repository");
const tpa_repository_1 = require("../tpa/tpa.repository");
const broker_repository_1 = require("../broker/broker.repository");
const response_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/response.utils");
const enum_1 = require("../../../../../../libs/service-lib/src/lib/enum");
const messages_1 = require("../../../../../../libs/service-lib/src/lib/messages");
const constants_1 = require("../../../../service-lib/src/lib/constants");
const constants_2 = require("../../../../../../libs/service-lib/src/lib/constants");
const lookup_validation_1 = require("../../../../service-lib/src/lib/utils/lookup-validation");
const helper_utils_1 = require("../../../../../../libs/service-lib/src/lib/utils/helper.utils");
/**
 * Service for managing contacts.
 */
let ContactService = class ContactService {
    constructor(contactRepository, addressRepository, // Inject AddressRepository
    companyRepository, // Inject CompanyRepository
    insurerRepository, // Inject InsurerRepository
    tpaRepository, // Inject InsurerRepository
    brokerRepository, // Inject InsurerRepository
    lookUpRepository, // Inject LookUpRepository
    lookUpValidation, dataSource // Inject DataSource for transaction manager
    ) {
        this.contactRepository = contactRepository;
        this.addressRepository = addressRepository;
        this.companyRepository = companyRepository;
        this.insurerRepository = insurerRepository;
        this.tpaRepository = tpaRepository;
        this.brokerRepository = brokerRepository;
        this.lookUpRepository = lookUpRepository;
        this.lookUpValidation = lookUpValidation;
        this.dataSource = dataSource;
    }
    /** Creates a new contact with address using transaction manager. */
    async addContact(createContact, userId) {
        return this.dataSource.transaction(async () => {
            try {
                if (!createContact.companyId)
                    throw new Error("Contact should have a company id");
                await this.lookUpValidation.validateDynamicLookupValues(createContact, constants_2.LOOK_UP_DATA);
                const recordTypeLookup = await this.lookUpRepository.getLookUpById(createContact.contactRecordTypeLid);
                const recordType = constants_1.CONTACT_RECORD_TYPE_MAP[recordTypeLookup.data
                    .lookUpKey];
                const companyConfig = await this.getCompanyConfig(recordType, createContact.companyId, userId);
                if (!companyConfig)
                    throw new Error("Unsupported record type");
                const companyData = await companyConfig.fetchMethod(createContact.companyId, userId);
                if (!companyData)
                    throw new Error(`${recordType} with ID ${createContact.companyId} not found`);
                const statusLid = companyConfig.statusIdKey
                    .split(".")
                    .reduce((object, key) => object && object[key] !== undefined ? object[key] : undefined, companyData);
                const companyStatus = await this.lookUpRepository.getLookUpById(Number(statusLid));
                if (companyStatus.data.lookUpValue !== constants_2.DEFAULT_ACTIVE_STATUS) {
                    throw new Error(`${recordType} with ID ${createContact.companyId} is not active`);
                }
                if (createContact?.communicationDetails?.length) {
                    await this.validateCommunication(createContact.communicationDetails, companyConfig, createContact.firstName);
                }
                let addressMapping = [];
                if (createContact?.address?.length) {
                    addressMapping = await this.processAddresses(createContact.address, companyData, companyConfig);
                }
                createContact.statusLid = (await this.lookUpRepository.findByLookUpKey(constants_2.DEFAULT_CONTACT_STATUS_ACTIVE_KEY))[0]?.id;
                (0, response_utils_1.addMetadataFields)(createContact, {
                    createdAt: new Date().toISOString(),
                    createdBy: userId,
                    updatedAt: new Date().toISOString(),
                    updatedBy: userId,
                });
                const contactResult = await this.contactRepository.createContact(createContact);
                contactResult.address = addressMapping;
                await this.createAddressMappings({
                    contactResult,
                    companyId: createContact.companyId,
                    addressMapping,
                    companyConfig,
                });
                const docDetails = await Promise.all(createContact.contactDocMaps?.map((doc) => this.contactRepository.createContactDocumentMapping({
                    contactId: contactResult.id,
                    documentId: doc.documentId,
                })) || []);
                const contactMappingInput = companyConfig.createCompanyMap(contactResult.id, createContact.companyId);
                await companyConfig.createContactMapping(contactMappingInput);
                contactResult.contactDocMaps = docDetails;
                contactResult.companyId = createContact.companyId;
                (0, response_utils_1.removeMetadataFields)(contactResult);
                const updatedContactResult = await this.contactRepository.getContactData(contactResult.id);
                // return contactResult;
                return updatedContactResult;
            }
            catch (error) {
                if (error?.message?.includes("Duplicate entry")) {
                    throw new Error(error?.message); // Propagate the duplicate entry error
                }
                throw error; // Re-throw other errors
            }
        });
    }
    async getCompanyConfig(recordType, companyId, userId) {
        const companyTypeMap = {
            [constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE]: {
                contactRecordTypeName: constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE,
                userId: userId,
                fetchMethod: this.companyRepository.getCompanyById.bind(this.companyRepository),
                statusIdKey: "status.id",
                defaultStatusKeyId: constants_2.DEFAULT_COMPANY_STATUS_UNDER_REVIEW_KEY_ID,
                communicationSearchQuery: {
                    communicationType: constants_2.DEFAULT_PHONE_KEY,
                    communicationDetails: constants_2.DEFAULT_PHONE_NUMBER,
                    contact: {
                        companyContactMaps: {
                            companyId: companyId,
                        },
                    },
                },
                addressRelation: "companyAddresses",
                createCompanyMap: (contactId, companyId) => ({
                    contactId,
                    companyId,
                }),
                createAddressMap: this.contactRepository.createCompanyAddressMapping.bind(this.contactRepository),
                findAddressMap: this.contactRepository.findCompanyAddressMap.bind(this.contactRepository),
                createContactMapping: this.contactRepository.createCompanyContactMapping.bind(this.contactRepository),
            },
            [constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE]: {
                contactRecordTypeName: constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE,
                userId: userId,
                fetchMethod: this.insurerRepository.fetchInsurerById.bind(this.insurerRepository),
                statusIdKey: "status.id",
                defaultStatusKeyId: constants_2.DEFAULT_INSURER_STATUS_ACTIVE_KEY_ID,
                communicationSearchQuery: {
                    communicationType: constants_2.DEFAULT_PHONE_KEY,
                    communicationDetails: constants_2.DEFAULT_PHONE_NUMBER,
                    contact: {
                        insurerContacts: {
                            insurerId: companyId,
                        },
                    },
                },
                addressRelation: "insurerAddresses",
                createCompanyMap: (contactId, companyId) => ({
                    contactId,
                    insurerId: companyId,
                }),
                createAddressMap: this.insurerRepository.addInsurerAddressMap.bind(this.insurerRepository),
                findAddressMap: this.insurerRepository.findInsurerCompanyAddressMap.bind(this.insurerRepository),
                createContactMapping: this.insurerRepository.addInsurerContact.bind(this.insurerRepository),
            },
            [constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE]: {
                contactRecordTypeName: constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE,
                userId: userId,
                fetchMethod: this.tpaRepository.getTpaById.bind(this.tpaRepository),
                statusIdKey: "status.id",
                defaultStatusKeyId: constants_2.DEFAULT_TPA_STATUS_ACTIVE_KEY_ID,
                communicationSearchQuery: {
                    communicationType: constants_2.DEFAULT_PHONE_KEY,
                    communicationDetails: constants_2.DEFAULT_PHONE_NUMBER,
                    contact: {
                        tpaContacts: {
                            id: companyId,
                        },
                    },
                },
                addressRelation: "tpaAddresses",
                createCompanyMap: (contactId, companyId) => ({
                    contactId,
                    id: companyId,
                }),
                createAddressMap: this.tpaRepository.addTpaAddress.bind(this.tpaRepository),
                findAddressMap: this.tpaRepository.findTpaCompanyAddressMap.bind(this.tpaRepository),
                createContactMapping: this.tpaRepository.addTpaContact.bind(this.tpaRepository),
            },
            [constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE]: {
                contactRecordTypeName: constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE,
                userId: userId,
                fetchMethod: this.brokerRepository.getBrokerById.bind(this.brokerRepository),
                statusIdKey: "status.id",
                defaultStatusKeyId: constants_2.DEFAULT_BROKER_STATUS_ACTIVE_KEY_ID,
                communicationSearchQuery: {
                    communicationType: constants_2.DEFAULT_PHONE_KEY,
                    communicationDetails: constants_2.DEFAULT_PHONE_NUMBER,
                    contact: {
                        brokerContacts: {
                            brokerId: companyId,
                        },
                    },
                },
                addressRelation: "brokerAddresses",
                createCompanyMap: (contactId, companyId) => ({
                    contactId,
                    brokerId: companyId,
                }),
                createAddressMap: this.brokerRepository.addBrokerAddressMap.bind(this.brokerRepository),
                findAddressMap: this.brokerRepository.findBrokerCompanyAddressMap.bind(this.brokerRepository),
                createContactMapping: this.brokerRepository.addBrokerContact.bind(this.brokerRepository),
            },
        };
        return companyTypeMap[recordType];
    }
    async createAddressMappings({ contactResult, companyId, addressMapping, companyConfig, }) {
        return Promise.all((addressMapping || []).map(async (addressData) => {
            const existingAddressMapping = await this.contactRepository.findContactAddressMap(contactResult.id, addressData.id);
            if (!existingAddressMapping) {
                await this.contactRepository.createContactAddressMapping({
                    contactId: contactResult.id,
                    addressId: addressData.id,
                });
            }
            // if a new contact address was being added it was also being added to its company address
            // commented the company address mapping as per the latest discussion
            // const addressTypeDetails = await this.lookUpRepository.getLookUpById(
            //   addressData.addressTypeId
            // );
            // if (
            //   addressTypeDetails.data.lookUpValue !==
            //   DEFAULT_RESIDENTIAL_ADDRESS_TYPE_KEY
            // ) {
            //   const existingCompanyMap = await companyConfig.findAddressMap(
            //     companyId,
            //     addressData.id
            //   );
            //   if (!existingCompanyMap) {
            //     const mapPayload: any = {
            //       address: addressData,
            //       addressId: addressData.id,
            //     };
            //     if (
            //       companyConfig.contactRecordTypeName === CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE
            //     ) {
            //       mapPayload.company = companyId;
            //     } else if (
            //       companyConfig.contactRecordTypeName === CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE
            //     ) {
            //       mapPayload.insurerId = companyId;
            //     } else if (
            //       companyConfig.contactRecordTypeName === CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE
            //     ) {
            //       mapPayload.id = companyId;
            //     } else if (
            //       companyConfig.contactRecordTypeName === CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE
            //     ) {
            //       mapPayload.brokerId = companyId;
            //     }
            //     await companyConfig.createAddressMap(mapPayload);
            //   }
            // }
        }));
    }
    async validateCommunication(details, config, contactName) {
        for (const detail of details) {
            const communicationQuery = {
                ...config.communicationSearchQuery,
                communicationType: detail.communicationType,
                communicationDetails: detail.communicationDetails,
                contactName: contactName,
            };
            if (detail.communicationType === constants_2.DEFAULT_PHONE_KEY) {
                const phoneCheck = await this.contactRepository.getCommunicationPhoneCheck(communicationQuery);
                if (phoneCheck.length !== 0) {
                    throw new Error(`Phone number ${detail.communicationDetails} already exists`);
                }
            }
            //  else if (detail.communicationType === DEFAULT_EMAIL_KEY) {
            //   const emailCheck =
            //     await this.contactRepository.getCommunicationEmailCheck(
            //       communicationQuery
            //     );
            //   if (emailCheck.length !== 0) {
            //     throw new Error(
            //       `Email ${detail.communicationDetails} already exists`
            //     );
            //   }
            // }
        }
    }
    async processAddresses(addresses, companyData, config) {
        const results = await Promise.all(addresses.map(async (addressDto) => {
            const addressExists = await this.checkCompanyAddressExists(addressDto, companyData[config.addressRelation]);
            if (addressExists === 0) {
                const metadata = {
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                    createdBy: config.userId,
                    updatedBy: config.userId,
                };
                Object.assign(addressDto, metadata);
                return await this.addressRepository.createAddress(addressDto);
            }
            else {
                return companyData[config.addressRelation].find((existingAddress) => existingAddress.id === addressExists);
            }
        }));
        return results;
    }
    /** Retrieves paginated contacts. */
    async getContactList(page, limit, userId, contactRecordTypeLid, search, sort, searchBy) {
        try {
            const searchParams = search ? (0, helper_utils_1.mapSearchParams)(search) : undefined;
            const sortParams = sort ? (0, helper_utils_1.mapSortParams)(sort) : undefined;
            const parentCompanyNameKey = "companyContactMaps.company.companyName";
            const parentCompanyBranchKey = "companyContactMaps.company.companyAddresses.address.cityId.name";
            const parentCompany = {
                [constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE]: "insurerContacts.insurer.insurerName",
                [constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE]: "tpaContacts.tpa.tpaName",
                [constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE]: "brokerContacts.broker.brokerName",
            };
            const parentCompanyBranch = {
                [constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE]: "insurerContacts.insurer.insurerAddresses.address.cityId.name",
                [constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE]: "tpaContacts.tpa.tpaAddresses.address.cityId.name",
                [constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE]: "brokerContacts.broker.brokerAddresses.address.cityId.name",
            };
            const parentCompanySortFields = {
                [constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE]: "companyContactMaps.company.companyName",
                [constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE]: "insurerContacts.insurer.insurerName",
                [constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE]: "tpaContacts.tpa.tpaName",
                [constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE]: "brokerContacts.broker.brokerName",
            };
            let globalSearchFields = constants_1.contactSearchObject, contactRecordTypeLookup;
            if (contactRecordTypeLid === constants_2.DEFAULT_COMPANY_CONTACT_RECORD_TYPE) {
                contactRecordTypeLookup = await this.lookUpRepository.findByLookUpKey(constants_2.DEFAULT_COMPANY_CONTACT_RECORD_TYPE_KEY);
                contactRecordTypeLid = contactRecordTypeLookup[0].id;
            }
            contactRecordTypeLookup = await this.lookUpRepository.getLookUpById(contactRecordTypeLid);
            const recordType = constants_1.CONTACT_RECORD_TYPE_MAP[contactRecordTypeLookup.data
                .lookUpKey];
            if (parentCompany[recordType]) {
                const parentCompanyMap = parentCompany[recordType];
                if (searchParams || searchBy) {
                    const parentCompanyBranchMap = parentCompanyBranch[recordType];
                    if (searchParams) {
                        const companyNameSearch = searchParams.find((item) => item.searchBy === parentCompanyNameKey);
                        if (companyNameSearch) {
                            companyNameSearch.searchBy = parentCompanyMap;
                        }
                        const companyBranchSearch = searchParams.find((item) => item.searchBy === parentCompanyBranchKey);
                        if (companyBranchSearch) {
                            companyBranchSearch.searchBy = parentCompanyBranchMap;
                        }
                    }
                }
                globalSearchFields = constants_1.contactSearchObject.map((field) => field === parentCompanyNameKey ? parentCompanyMap : field);
            }
            if (sortParams) {
                const parentCompanySort = parentCompanySortFields[recordType];
                const companySortOrderSearch = sortParams.find((item) => item.field === constants_1.sortRealtionsMapping.PARENT_COMPANY_NAME_SORT_KEY);
                if (companySortOrderSearch) {
                    companySortOrderSearch.field = parentCompanySort;
                }
            }
            if (searchParams) {
                const parentCompanySort = parentCompanySortFields[recordType];
                const companySortOrderSearch = searchParams.find((item) => item.searchBy === constants_1.sortRealtionsMapping.PARENT_COMPANY_NAME_SORT_KEY);
                if (companySortOrderSearch) {
                    companySortOrderSearch.searchBy = parentCompanySort;
                }
            }
            const contactData = await this.contactRepository.fetchPaginatedContactList(page, limit, userId, contactRecordTypeLid, searchParams, sortParams, searchBy, globalSearchFields);
            const status = await this.lookUpRepository.findByLookUpKey(constants_2.DEFAULT_CONTACT_STATUS_ACTIVE_KEY);
            const whereCondition = {
                createdBy: userId,
                contactRecordTypeLid: contactRecordTypeLid,
                statusLid: status[0].id ?? undefined,
            };
            const totalContacts = contactData?.data?.length > 0
                ? await this.contactRepository.getTotalContacts(whereCondition, globalSearchFields, searchParams, searchBy)
                : 0;
            const totalCompanies = contactData?.data?.length > 0
                ? await this.contactRepository.getTotalCompanies(whereCondition, searchBy, searchParams)
                : 0;
            return {
                data: contactData.data,
                count: contactData.count,
                totalContacts,
                totalCompanies,
            };
        }
        catch (error) {
            throw new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.badRequest, messages_1.errorMessages.contactFetchFailed));
        }
    }
    /** Retrieves a contact by ID. */
    async getContactById(contactId) {
        try {
            const contact = await this.contactRepository.getContactData(contactId);
            if (!contact) {
                throw new common_1.NotFoundException(`Contact with ID ${contactId} not found`);
            }
            (0, response_utils_1.removeMetadataFields)(contact);
            return contact;
        }
        catch (error) {
            throw new common_1.InternalServerErrorException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.badRequest, messages_1.errorMessages.contactFetchFailed));
        }
    }
    /** Updates a contact by ID using transaction manager. */
    async updateContactById(contactId, updateContact, userId) {
        return this.dataSource.transaction(async () => {
            try {
                await this.lookUpValidation.validateDynamicLookupValues(updateContact, constants_2.LOOK_UP_DATA);
                const existingContactDetails = await this.contactRepository.getExistingContactDetails(contactId);
                if (!existingContactDetails) {
                    throw new common_1.NotFoundException(`Contact with ID ${contactId} not found`);
                }
                if (updateContact?.reportingToId &&
                    updateContact?.reportingToId === contactId) {
                    throw new common_1.BadRequestException(`The 'Reporting To' field cannot reference the same contact.`);
                }
                const { qualificationExperiences, professionalExperiences, contactDetails, address, communicationDetails, contactDocMaps, ...contactPayload } = updateContact;
                const existingContactData = await this.contactRepository.getContactCompanyDataAddress(contactId);
                const recordType = constants_1.CONTACT_RECORD_TYPE_MAP[(existingContactData.contactRecordType?.lookUpKey ??
                    existingContactData.contactRecordType?.data
                        ?.lookUpKey)];
                if ("companyId" in contactPayload) {
                    if (contactPayload.companyId === null) {
                        const [contactStatus] = await this.lookUpRepository.findByLookUpKey(constants_2.DEFAULT_CONTACT_STATUS_INACTIVE_KEY);
                        contactPayload.statusLid = contactStatus.id;
                        if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE) {
                            const companyContactObject = {
                                contactId: contactId,
                                companyId: existingContactData.company.id,
                            };
                            await this.contactRepository.delinkCompanyContactMapping(companyContactObject);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE) {
                            const insurerContactObject = {
                                contactId: contactId,
                                insurerId: existingContactData.company.id,
                            };
                            await this.insurerRepository.delinkInsurerContactMapping(insurerContactObject);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE) {
                            const tpaContactObject = {
                                contactId: contactId,
                                id: existingContactData.company.id,
                            };
                            await this.tpaRepository.delinkTpaContactMapping(tpaContactObject);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE) {
                            const brokerContactObject = {
                                contactId: contactId,
                                brokerId: existingContactData.company.id,
                            };
                            await this.brokerRepository.delinkBrokerContactMap(brokerContactObject);
                        }
                        delete contactPayload.companyId;
                    }
                    else if (contactPayload.companyId !== undefined) {
                        let companyData;
                        if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE) {
                            companyData = await this.companyRepository.getCompanyById(contactPayload.companyId, userId);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE) {
                            companyData =
                                await this.insurerRepository.fetchInsurerByIdWithDetails(contactPayload.companyId);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE) {
                            companyData = await this.tpaRepository.getTpaById(contactPayload.companyId);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE) {
                            companyData = await this.brokerRepository.fetchBrokerById(contactPayload.companyId);
                        }
                        if (!companyData) {
                            throw new Error(`Company with ID ${contactPayload.companyId} not found`);
                        }
                        const companyStatus = await this.lookUpRepository.getLookUpById(companyData.status?.id ??
                            constants_2.DEFAULT_COMPANY_STATUS_UNDER_REVIEW_KEY_ID);
                        const statusKey = companyStatus.data.lookUpValue === constants_2.DEFAULT_ACTIVE_STATUS
                            ? constants_2.DEFAULT_CONTACT_STATUS_ACTIVE_KEY
                            : constants_2.DEFAULT_CONTACT_STATUS_INACTIVE_KEY;
                        const [contactStatus] = await this.lookUpRepository.findByLookUpKey(statusKey);
                        if (statusKey === constants_2.DEFAULT_CONTACT_STATUS_INACTIVE_KEY)
                            contactPayload.statusLid = contactStatus.id;
                        if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.COMPANY_CONTACT_RECORD_TYPE) {
                            const companyContactObjectLink = {
                                contactId: contactId,
                                companyId: contactPayload.companyId,
                            };
                            if (existingContactData?.company !== null) {
                                const companyContactObjectDelink = {
                                    contactId: contactId,
                                    companyId: existingContactData.company.id,
                                };
                                await this.contactRepository.delinkCompanyContactMapping(companyContactObjectDelink);
                            }
                            await this.contactRepository.createCompanyContactMapping(companyContactObjectLink);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.INSURER_CONTACT_RECORD_TYPE) {
                            const insurerContactObjectLink = {
                                contactId: contactId,
                                insurerId: contactPayload.companyId,
                            };
                            if (existingContactData?.company !== null) {
                                const insurerContactObjectDelink = {
                                    contactId: contactId,
                                    insurerId: existingContactData.company.id,
                                };
                                await this.insurerRepository.delinkInsurerContactMapping(insurerContactObjectDelink);
                            }
                            await this.insurerRepository.addInsurerContact(insurerContactObjectLink);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.TPA_CONTACT_RECORD_TYPE) {
                            const tpaContactObjectLink = {
                                contactId: contactId,
                                id: contactPayload.companyId,
                            };
                            if (existingContactData?.company !== null) {
                                const tpaContactObjectDelink = {
                                    contactId: contactId,
                                    id: existingContactData.company.id,
                                };
                                await this.tpaRepository.delinkTpaContactMapping(tpaContactObjectDelink);
                            }
                            await this.tpaRepository.addTpaContact(tpaContactObjectLink);
                        }
                        else if (recordType === constants_1.CONTACT_RECORD_TYPE_MAP.BROKER_CONTACT_RECORD_TYPE) {
                            const brokerContactObjectLink = {
                                contactId: contactId,
                                brokerId: contactPayload.companyId,
                            };
                            if (existingContactData?.company !== null) {
                                const brokerContactObjectDelink = {
                                    contactId: contactId,
                                    brokerId: existingContactData.company.id,
                                };
                                await this.brokerRepository.delinkBrokerContactMap(brokerContactObjectDelink);
                            }
                            await this.brokerRepository.addBrokerContact(brokerContactObjectLink);
                        }
                        delete contactPayload.companyId;
                    }
                }
                let addressExists = 0;
                contactPayload.updatedAt = new Date();
                contactPayload.updatedBy = userId;
                const contact = await this.contactRepository.updateContact(contactId, contactPayload);
                if (!contact) {
                    throw new common_1.NotFoundException(`Contact with ID ${contactId} not found`);
                }
                const metadata = {
                    createdAt: new Date().toISOString(),
                    createdBy: userId,
                    updatedAt: new Date().toISOString(),
                    updatedBy: userId,
                };
                if (address) {
                    // retrieves the existing address
                    const existingAddressIds = await this.contactRepository.getEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.CONTACT_ADDRESS, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ADDRESS_ID, { contactId: contactId });
                    if (address?.length > 0) {
                        const updatedAddresses = await Promise.all(address.map(async (addr) => {
                            if (addr.id) {
                                const index = existingAddressIds.indexOf(addr.id);
                                if (index !== -1) {
                                    existingAddressIds.splice(index, 1);
                                }
                                addr.updatedAt = new Date();
                                addr.updatedBy = userId;
                                return this.addressRepository.updateAddressDetails(addr.id, addr);
                            }
                            else {
                                addressExists = await this.checkCompanyAddressExists(addr, existingContactData?.company?.companyAddresses);
                                if (addressExists === 0) {
                                    Object.assign(addr, metadata);
                                    return this.addressRepository.createAddress(addr);
                                }
                                else {
                                    return existingContactData.company.companyAddresses.find((existingContactAddress) => existingContactAddress?.address?.id === addressExists)?.address;
                                }
                            }
                        }));
                        contact.address = updatedAddresses;
                        await Promise.all(updatedAddresses.map(async (addressData) => {
                            const existingMapping = await this.contactRepository.findContactAddressMap(contact.id, addressData.id);
                            if (!existingMapping) {
                                const mappingData = {
                                    contactId: contact.id,
                                    addressId: addressData.id,
                                };
                                await this.contactRepository.createContactAddressMapping(mappingData);
                            }
                        }));
                    }
                    // remove all existing address.
                    await this.contactRepository.deleteEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.CONTACT_ADDRESS, existingAddressIds, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ADDRESS_ID, constants_2.MAPPED_DATA_DELETION.MAP_REMOVED);
                }
                if (qualificationExperiences) {
                    // retrieves the existing qualification experience
                    const existingQualificationIds = await this.contactRepository.getEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.QUALIFICATION_EXPERIENCE, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, { contact: { id: contactId } });
                    if (qualificationExperiences?.length > 0) {
                        const updatedQualifications = await Promise.all(qualificationExperiences.map((qualification) => {
                            if (qualification.id) {
                                const index = existingQualificationIds.indexOf(qualification.id);
                                if (index !== -1) {
                                    existingQualificationIds.splice(index, 1);
                                }
                                qualification.updatedAt = new Date();
                                qualification.updatedBy = userId;
                                return this.contactRepository.updateQualificationExperience(qualification.id, qualification);
                            }
                            else {
                                Object.assign(qualification, metadata);
                                qualification.contact = contactId;
                                return this.contactRepository.createQualificationExperience(qualification);
                            }
                        }));
                        contact.qualificationExperiences = updatedQualifications;
                    }
                    // remove all existing qualification experience.
                    await this.contactRepository.deleteEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.QUALIFICATION_EXPERIENCE, existingQualificationIds, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, constants_2.MAPPED_DATA_DELETION.SOFT_DELETE);
                }
                if (professionalExperiences) {
                    // retrieves the existing professional experience
                    const existingProfessionalIds = await this.contactRepository.getEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.PROFESSIONAL_EXPERIENCE, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, { contact: { id: contactId } });
                    if (professionalExperiences?.length > 0) {
                        const updatedProfessionalExperiences = await Promise.all(professionalExperiences.map((experience) => {
                            if (experience.id) {
                                const index = existingProfessionalIds.indexOf(experience.id);
                                if (index !== -1) {
                                    existingProfessionalIds.splice(index, 1);
                                }
                                experience.updatedAt = new Date();
                                experience.updatedBy = userId;
                                return this.contactRepository.updateProfessionalExperience(experience.id, experience);
                            }
                            else {
                                Object.assign(experience, metadata);
                                experience.contact = contactId;
                                return this.contactRepository.createProfessionalExperience(experience);
                            }
                        }));
                        contact.professionalExperiences = updatedProfessionalExperiences;
                    }
                    // remove all existing professional experience.
                    await this.contactRepository.deleteEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.PROFESSIONAL_EXPERIENCE, existingProfessionalIds, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, constants_2.MAPPED_DATA_DELETION.SOFT_DELETE);
                }
                if (contactDetails) {
                    const childMetadata = {
                        ...metadata,
                        contactDetailsId: contactDetails?.id,
                    };
                    if (contactDetails.id) {
                        contactDetails.updatedAt = new Date();
                        contactDetails.updatedBy = userId;
                        const children = contactDetails.childDetails ?? [];
                        // retreive all existing child details
                        const existingChildIds = await this.contactRepository.getEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.CHILD_DETAILS, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, { contactDetailsId: contactDetails.id });
                        if (children.length > 0) {
                            await Promise.all(children.map(async (childDetail) => {
                                if (childDetail.id) {
                                    const index = existingChildIds.indexOf(childDetail.id);
                                    if (index !== -1) {
                                        existingChildIds.splice(index, 1);
                                    }
                                }
                                const baseData = childDetail.id
                                    ? { ...childDetail, ...childMetadata }
                                    : {
                                        ...childDetail,
                                        ...metadata,
                                        contactDetailsId: contactDetails.id,
                                    };
                                return childDetail.id
                                    ? this.contactRepository.updateChildDetails(childDetail.id, baseData)
                                    : this.contactRepository.createChildDetails(baseData);
                            }));
                        }
                        delete contactDetails.childDetails; // Clean up before saving
                        await this.contactRepository.updateContactDetails(contactDetails.id, contactDetails);
                        // remove all existing child details
                        await this.contactRepository.deleteEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.CHILD_DETAILS, existingChildIds, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, constants_2.MAPPED_DATA_DELETION.SOFT_DELETE);
                    }
                    else {
                        Object.assign(contactDetails, metadata);
                        if (Array.isArray(contactDetails.childDetails) &&
                            contactDetails.childDetails.length > 0) {
                            const now = new Date();
                            contactDetails.childDetails = contactDetails.childDetails.map((childDetail) => ({
                                ...childDetail,
                                ...metadata,
                                createdAt: now,
                                updatedAt: now,
                            }));
                        }
                        contactDetails.contact = contactId;
                        await this.contactRepository.createContactDetails(contactDetails);
                    }
                    contact.contactDetails = contactDetails;
                }
                if (communicationDetails) {
                    // retrieves the existing communication details
                    const existingCommunicationDetailsIds = await this.contactRepository.getEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.COMMUNICATION_DETAILS, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, { contactId: contactId });
                    if (communicationDetails?.length) {
                        const updatedCommunicationDetails = await Promise.all(communicationDetails.map((detail) => {
                            if (detail.id) {
                                const index = existingCommunicationDetailsIds.indexOf(detail.id);
                                if (index !== -1) {
                                    existingCommunicationDetailsIds.splice(index, 1);
                                }
                                detail.updatedAt = new Date();
                                detail.updatedBy = userId;
                                return this.contactRepository.updateCommunicationDetails(detail.id, detail);
                            }
                            else {
                                Object.assign(detail, metadata);
                                detail.contact = contactId;
                                return this.contactRepository.createContactCommunicationDetails(detail);
                            }
                        }));
                        contact.communicationDetails = updatedCommunicationDetails;
                    }
                    // remove all existing communication details.
                    await this.contactRepository.deleteEntityTableMapIds(constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.COMMUNICATION_DETAILS, existingCommunicationDetailsIds, constants_1.CONTACT_MAP_TABLE_DELETE_FIELDS.ID, constants_2.MAPPED_DATA_DELETION.SOFT_DELETE);
                }
                if (contactDocMaps?.length) {
                    const updatedContactDocMaps = await Promise.all(contactDocMaps.map((docDetails) => {
                        if (docDetails.id) {
                            docDetails.updatedAt = new Date();
                            return this.contactRepository.updateContactDocumentDetails(docDetails.id, docDetails);
                        }
                        else {
                            const newDocObject = {
                                contactId: contactId,
                                documentId: docDetails.documentId,
                            };
                            return this.contactRepository.createContactDocumentMapping(newDocObject);
                        }
                    }));
                    contact.contactDocMaps = updatedContactDocMaps;
                }
                (0, response_utils_1.removeMetadataFields)(contact);
                const updatedContactResult = await this.contactRepository.getContactData(contactId);
                return updatedContactResult;
                // return contact;
            }
            catch (error) {
                if (error.message.includes("Duplicate entry")) {
                    throw new Error(error.message); // Propagate the duplicate entry error
                }
                throw error; // Re-throw other errors
            }
        });
    }
    /** Removes a contact by ID. */
    async deleteContactById(contactId) {
        return this.dataSource.transaction(async () => {
            try {
                const contact = await this.contactRepository.getContactData(contactId);
                if (!contact) {
                    throw new common_1.NotFoundException(`Contact with ID ${contactId} not found`);
                }
                await this.contactRepository.removeContact(contact);
                return {
                    message: `Contact with ID ${contactId} and its addresses deleted successfully`,
                };
            }
            catch (error) {
                throw new common_1.BadRequestException((0, response_utils_1.createErrorResponse)(enum_1.statusCode.badRequest, messages_1.errorMessages.contactDeletionFailed));
            }
        });
    }
    /** Checks the given new address exists in company address */
    async checkCompanyAddressExists(newAddress, companyAddresses) {
        const addressObject = [
            "address1",
            "cityId",
            "stateId",
            "countryId",
            "pinCode",
            "phoneNumber",
        ];
        // Fetch address type details
        const addressTypeDetails = await this.lookUpRepository.getLookUpById(newAddress.addressTypeLid);
        // If it's a default residential address, return 0
        if (addressTypeDetails.data.lookUpValue ===
            constants_2.DEFAULT_RESIDENTIAL_ADDRESS_TYPE_KEY) {
            return 0;
        }
        if (companyAddresses?.length) {
            for (const existingAddress of companyAddresses) {
                // Check if all specified keys in `addressObject` match
                const isExactMatch = addressObject.every((key) => ["countryId", "stateId", "cityId"].includes(key)
                    ? existingAddress[key]["id"] === newAddress[key]
                    : existingAddress[key] === newAddress[key]
                // Object.keys(existingAddress).includes(key) ? existingAddress[key] === newAddress[key]: existingAddress["address"][key] === newAddress[key]
                );
                if (isExactMatch) {
                    return existingAddress.address.id; // Return ID if an exact match is found
                }
            }
        }
        return 0; // Return 0 if no match is found
    }
};
exports.ContactService = ContactService;
exports.ContactService = ContactService = tslib_1.__decorate([
    (0, common_1.Injectable)(),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof contact_repository_1.ContactRepository !== "undefined" && contact_repository_1.ContactRepository) === "function" ? _a : Object, typeof (_b = typeof address_repository_1.AddressRepository !== "undefined" && address_repository_1.AddressRepository) === "function" ? _b : Object, typeof (_c = typeof company_repository_1.CompanyRepository !== "undefined" && company_repository_1.CompanyRepository) === "function" ? _c : Object, typeof (_d = typeof insurer_repository_1.InsurerRepository !== "undefined" && insurer_repository_1.InsurerRepository) === "function" ? _d : Object, typeof (_e = typeof tpa_repository_1.TpaRepository !== "undefined" && tpa_repository_1.TpaRepository) === "function" ? _e : Object, typeof (_f = typeof broker_repository_1.BrokerRepository !== "undefined" && broker_repository_1.BrokerRepository) === "function" ? _f : Object, typeof (_g = typeof look_up_repository_1.LookUpRepository !== "undefined" && look_up_repository_1.LookUpRepository) === "function" ? _g : Object, typeof (_h = typeof lookup_validation_1.LookUpValidationService !== "undefined" && lookup_validation_1.LookUpValidationService) === "function" ? _h : Object, typeof (_j = typeof typeorm_1.DataSource // Inject DataSource for transaction manager
         !== "undefined" && typeorm_1.DataSource // Inject DataSource for transaction manager
        ) === "function" ? _j : Object])
], ContactService);
